<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-container class="pa-0">
              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    Preview
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col v-for="input in previewInputs" :key="input.key">
                        <select-list
                          v-if="input.enum && input.enum.length"
                          v-model="preview.inputs[input.key]"
                          :hint="input.description"
                          :items="input.enum"
                          :label="input.name"
                          :required="input.required"
                          clearable
                          persistent-hint
                        />
                        <text-input
                          v-else
                          v-model="preview.inputs[input.key]"
                          :hint="input.description"
                          :label="input.name"
                          :required="input.required"
                          clearable
                          persistent-hint
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-card outlined>
                          <v-card-text>
                            <pre>{{ interpolatedScript }}</pre>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-text>
              <text-input
                v-model="formData.name"
                :disabled="!isPermittedTo('update') || isLoading"
                :hint="hints.name"
                class="mb-6"
                label="Name"
                persistent-hint
                required
              />
              <textarea-input
                v-model="formData.description"
                :disabled="!isPermittedTo('update') || isLoading"
                :hint="hints.description"
                class="mb-6"
                label="Description"
                rows="2"
                persistent-hint
                required
              />
              <textarea-input
                v-model="formData.script"
                :disabled="!isPermittedTo('update') || isLoading"
                :hint="hints.script"
                class="mb-4"
                label="Script"
                rows="3"
                persistent-hint
              />
              <div class="d-flex align-center text-caption mb-3">
                <v-icon left small>info</v-icon>
                <span
                  >If AMP Script is left empty, the shortcode will not appear for
                  <a href="https://en.wikipedia.org/wiki/Accelerated_Mobile_Pages" target="_blank" rel="nofollow noopen"
                    >AMP</a
                  >
                  users.</span
                >
              </div>
              <textarea-input
                v-model="formData.script_amp"
                :disabled="!isPermittedTo('update') || isLoading"
                :hint="hints.script_amp"
                class="mb-6"
                label="AMP Script"
                rows="2"
                persistent-hint
              />
              <v-card outlined>
                <v-card-text>
                  <div class="d-flex align-center justify-space-between mb-6">
                    <div class="body-2">Inputs</div>
                    <v-btn
                      color="primary"
                      :disabled="!isPermittedTo('update') || isLoading"
                      depressed
                      small
                      @click="onAddInput"
                      >+ Add input</v-btn
                    >
                  </div>
                  <v-card
                    v-for="(input, inputIndex) in formData.inputs"
                    :key="inputIndex"
                    :class="inputIndex && 'mt-6'"
                    outlined
                  >
                    <v-card-text>
                      <v-row>
                        <v-col sm="4"
                          ><text-input
                            v-model="input.name"
                            :disabled="!isPermittedTo('update') || isLoading"
                            label="Name"
                            hide-details
                            required
                        /></v-col>
                        <v-col sm="4"
                          ><text-input
                            v-model="input.key"
                            :disabled="!isPermittedTo('update') || isLoading"
                            label="Key"
                            hide-details
                            required
                        /></v-col>
                        <v-col class="d-flex align-center">
                          <toggle-input
                            v-model="input.required"
                            :is-disabled="!isPermittedTo('update') || isLoading"
                            label="Required"
                            hide-details
                            required
                          />
                        </v-col>
                        <v-col sm="auto" class="d-flex align-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                color="error"
                                :disabled="!isPermittedTo('update') || isLoading"
                                depressed
                                @click="onDeleteInput(inputIndex)"
                                v-on="on"
                              >
                                <v-icon>delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this input</span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <text-input
                            v-model="input.description"
                            :disabled="!isPermittedTo('update') || isLoading"
                            label="Description"
                            hide-details
                            required
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-card outlined>
                            <v-card-text>
                              <div
                                :class="input.enum && input.enum.length && 'mb-6'"
                                class="d-flex align-center justify-space-between"
                              >
                                <div class="body-2">Options</div>
                                <v-btn
                                  :disabled="!isPermittedTo('update') || isLoading"
                                  color="primary"
                                  depressed
                                  small
                                  @click="onAddOption(inputIndex)"
                                >
                                  + Add option
                                </v-btn>
                              </div>
                              <v-row v-for="(option, optionIndex) in input.enum" :key="optionIndex">
                                <v-col>
                                  <text-input
                                    v-model="option.key"
                                    :disabled="!isPermittedTo('update') || isLoading"
                                    :hint="hints.optionKey"
                                    label="Key"
                                    persistent-hint
                                    required
                                  />
                                </v-col>

                                <v-col>
                                  <text-input
                                    v-model="option.value"
                                    :disabled="!isPermittedTo('update') || isLoading"
                                    :hint="hints.optionValue"
                                    label="Value"
                                    persistent-hint
                                    required
                                  />
                                </v-col>
                                <v-col sm="auto">
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                      <v-btn
                                        :disabled="!isPermittedTo('update') || isLoading"
                                        color="error"
                                        depressed
                                        @click="onDeleteOption(inputIndex, optionIndex)"
                                        v-on="on"
                                      >
                                        <v-icon>delete</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>Delete this option</span>
                                  </v-tooltip>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <fab-actions
      :can-delete="isPermittedTo('delete') && pageType === 'edit'"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Shortcode"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
      @delete="showDeleteModal = true"
    />

    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="Shortcode"
      @modal-confirm="onResetConfirm"
    />

    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Shortcode"
      persistent
      :is-confirm-disabled="!hasAllRequiredFields"
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <span v-if="!hasAllRequiredFields">Missing required fields!</span>
      <span v-else-if="pageType === 'edit'">Are you sure you want to save these changes?</span>
    </generic-modal>

    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Shortcode"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this shortcode? This cannot be
        undone.
      </div>
    </generic-modal>

    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { cloneDeep, kebabCase } from 'lodash-es'
import { mapGetters, mapMutations } from 'vuex'
// Components
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import SelectList from '@/components/forms/select-list.vue'
import TextareaInput from '@/components/forms/textarea-input'
import TextInput from '@/components/forms/text-input'
import ToggleInput from '@/components/forms/toggle-input'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import RedirectHandler from '@/mixins/redirect-handler'
// Services
import { shortcodes } from '@/services/offers'

const newInput = ({ name = null, key = null, description = null, required = false } = {}) => ({
  name,
  key,
  description,
  required,
})

const newInputOption = ({ key = null, value = null } = {}) => ({
  key,
  value,
})

export default {
  name: 'ShortcodeEditor',
  components: {
    FabActions,
    GenericModal,
    PageLoader,
    ResetModal,
    SelectList,
    TextareaInput,
    TextInput,
    ToggleInput,
    ExitModal,
  },
  mixins: [DataModification, PermissionsHandler, RedirectHandler],
  data: () => ({
    isLoading: false,
    hints: {
      name: 'What is the name of the script? This should be simple and quick for users to identify',
      description: "Give some information about the script so that users can easily understand what it's used for",
      script:
        'Sections that are customisable should use the {{ }} templating syntax. Eg. for "test_key" you\'d enter {{test_key}}, and define it in the "Key" field for an input below',
      script_amp:
        'AMP-compatible script. Sections that are customisable should use the {{ }} templating syntax. Eg. for "test_key" you\'d enter {{test_key}}, and define it in the "Key" field for an input below',
      optionKey: 'The value seen by the user',
      optionValue: 'The value passed into the script',
    },
    original: {
      name: null,
      description: null,
      inputs: [],
      script: '',
    },
    formData: {
      name: null,
      description: null,
      inputs: [],
      script: '',
    },
    preview: {
      inputs: {},
    },
    showResetModal: false,
    showSaveModal: false,
    showDeleteModal: false,
    showExitModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    pageType() {
      return this.$route.meta.pageType
    },
    breadcrumbs() {
      return [
        { text: 'Shortcode Management', to: { name: 'shortcodes.index' }, exact: true },
        this.pageType === 'create' && { text: 'Create', disabled: true },
        this.pageType === 'edit' && { text: this.original.name || 'loading...', disabled: true },
      ].filter(Boolean)
    },
    interpolatedScript() {
      return this.formData.inputs.reduce(
        (script, { key }) => script.replaceAll(`{{${key}}}`, this.preview.inputs[key] || ''),
        this.formData.script
      )
    },
    previewInputs() {
      return this.formData.inputs.filter(({ key }) => key && Boolean(key.trim()))
    },
    hasChanges() {
      return this.hasDataChanged(this.original, this.formData)
    },
    hasAllRequiredFields() {
      const { name, description, script, inputs } = this.formData
      const inputValues = inputs.flatMap(({ name, key, description }) => [name, key, description])
      return [name, description, script, ...inputValues].every(val => val && Boolean(val.trim()))
    },
  },
  async created() {
    if (this.pageType === 'edit') await this.getShortcode()
    else this.setPageLoading(false)
    this.setBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getShortcode() {
      this.isLoading = true
      try {
        const data = await shortcodes.getSingle(this.$route.params.id)
        this.original = data
        this.formData = cloneDeep(data)
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve shortcode',
        })
        this.$_redirectHandler_404({ name: 'shortcodes.index' })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
    async createShortcode() {
      try {
        this.isLoading = true
        const { id } = await shortcodes.createShortcode(this.formData)
        this.original = cloneDeep(this.formData)
        this.$notify({ type: 'success', text: 'Successfully created shortcode' })
        this.$router.push({ name: 'shortcodes.edit', params: { id } })
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.$notify({ type: 'error', text: 'Unable to update shortcode', data: { error }, duration: -1 })
      }
    },
    async updateShortcode() {
      try {
        this.isLoading = true
        const trimmedScriptAmp = this.formData.script_amp?.trim()?.replace(/^\s+|\s+$/g, '') || null
        await shortcodes.updateShortcode(this.$route.params.id, { ...this.formData, script_amp: trimmedScriptAmp })
        this.$notify({ type: 'success', text: 'Successfully updated shortcode' })
        await this.getShortcode()
        this.isLoading = false
      } catch (e) {
        this.isLoading = false
        this.$notify({ type: 'error', text: 'Unable to update shortcode', data: { error: e }, duration: -1 })
      }
    },
    async deleteShortcode() {
      try {
        this.isLoading = true
        await shortcodes.deleteShortcode(this.$route.params.id)
        this.$notify({ type: 'success', text: 'Successfully deleted shortcode' })
        this.$router.push({ name: 'shortcodes.index' })
        this.isLoading = false
      } catch (e) {
        this.isLoading = false
        this.$notify({ type: 'error', text: 'Unable to delete shortcode', data: { error: e }, duration: -1 })
      }
    },
    onAddInput() {
      this.formData.inputs.push(newInput())
    },
    onDeleteInput(index) {
      const updated = this.formData.inputs.slice(0, index).concat(this.formData.inputs.slice(index + 1))
      this.$set(this.formData, 'inputs', updated)
    },
    onAddOption(inputIndex) {
      if (!this.formData.inputs[inputIndex].enum)
        this.$set(this.formData.inputs[inputIndex], 'enum', [newInputOption()])
      else this.formData.inputs[inputIndex].enum.push(newInputOption())
    },
    onDeleteOption(inputIndex, optionIndex) {
      const updated = this.formData.inputs[inputIndex].enum
        .slice(0, optionIndex)
        .concat(this.formData.inputs[inputIndex].enum.slice(optionIndex + 1))
      this.$set(this.formData.inputs[inputIndex], 'enum', updated)
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    onResetConfirm() {
      this.formData = cloneDeep(this.original)
      this.showResetModal = false
    },
    async onSaveConfirm() {
      if (this.pageType === 'edit') await this.updateShortcode()
      else await this.createShortcode()
      this.showSaveModal = false
    },
    async onDeleteConfirm() {
      await this.deleteShortcode()
    },
  },
}
</script>
